import Avatar from '@material-ui/core/Avatar';
import Chip from '@material-ui/core/Chip';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import GS from '../grafsync/grafsync';
import Errored from './Errored';
import { globalStyles } from './style/gstheme';
import { makeCancelable } from './utils/utils';

const styles = theme => {
    return Object.assign({
        link: {
            color: 'white',
            marginRight: 10,
            marginBottom: 10,
            maxWidth: '100%',
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
                cursor: 'pointer'
            },
            '&:focus-visible': {
                outline: 'dashed 2px black'
            }
        },
        superCompactLink: {
            color: 'white',
            marginRight: 10,
            marginBottom: 0,
            maxWidth: '100%',
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
                cursor: 'pointer'
            },
            '&:focus-visible': {
                outline: 'dashed 2px black'
            }
        },
        compactLink: {
            color: 'white',
            marginRight: 10,
            marginBottom: 6,
            maxWidth: '100%',
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
                cursor: 'pointer'
            },
            '&:focus-visible': {
                outline: 'dashed 2px black'
            }
        }
    }, globalStyles);
};

class ExternalResourceLink extends React.Component {

    handleDeleteLink() {
        const graph = this.props.sourceGraph || this.props.graph;
        // If a delete function supplied call
        if (this.props.onDeleteIRI) {
            this.props.onDeleteIRI(this.props.id);
        }
        else {
            // Else we do this directly to ensure direction of link delete is correct 
            GS.deleteProperty(this.props.source, graph, this.props.property, this.props.id, null, null, this.props.propertyGraph);
        }
    }

    constructor(props) {
        super(props);
        this.state = {
            showInfoDetails: false,
            isErrored: null,
            anchorEl: null,
            hideImage: false
        };
        this.cancellable = null;
    }

    loadData(id) {
        this.cancellable = makeCancelable(GS.doGetRequest(id));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    hideImage = () => this.setState({ hideImage: true });

    clearError = () => this.setState({ isErrored: false });

    render() {

        const { classes, isReadOnly, id, dataTest, source, invalid, annotated, compact, highlight, deleted, superCompact } = this.props;
        const { anchorEl, data, isErrored, hideImage } = this.state;
        const isLinkedData = id.includes("dbpedia.org");

        if (isErrored) return <Errored dataTest="el-error" data={isErrored} clearError={this.clearError} />;

        return (
            <React.Fragment>
                <Tooltip title={deleted ?
                    "This has been deleted or is no longer available"
                    :
                    <span style={{ display: 'block', marginTop: 6, marginBottom: 6 }}>{id}</span>}>
                    <Chip
                        data-test={dataTest}
                        data-deleted={deleted ? true : null}
                        color="primary"
                        className={superCompact ?
                            classes.superCompactLink
                            :
                            compact ?
                                classes.compactLink
                                :
                                classes.link}
                        style={{
                            ...(annotated) && {
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 10
                            },
                            ...(invalid || highlight) && {
                                backgroundColor: id.startsWith("http://www.w3.org/2002/07/owl#") ?
                                    '#767676'
                                    :
                                    '#CA4F0C'
                            }
                        }}
                        avatar={
                            <Avatar style={{ backgroundColor: 'inherit' }}>
                                <a
                                    aria-label={"External link to " + id}
                                    aria-hidden={true}
                                    href={id}
                                    target="_blank" rel="noreferrer">
                                    <OpenInNewIcon fontSize="small" />
                                </a>
                            </Avatar>
                        }
                        onDelete={!isReadOnly && source ?
                            ((ev) => {
                                this.handleDeleteLink();
                                ev.preventDefault();
                                ev.stopPropagation();
                            })
                            :
                            null
                        }
                        label={<span>
                            <a aria-label="External link" href={id} target="_new" data-test={invalid ? "invalid-link" : "valid-link"}>{
                                id.indexOf("http://www.w3.org/2001/XMLSchema#") === 0 ?
                                    "XSD " + id.substring(33)
                                    :
                                    id.indexOf("http://www.w3.org/1999/02/22-rdf-syntax-ns#") === 0 ?
                                        "RDF " + id.substring(43)
                                        :
                                        id
                            }</a>
                        </span>} />
                </Tooltip>
            </React.Fragment>
        );
    }
}

ExternalResourceLink.defaultProps = {
    isReadOnly: false,
    invalid: false,
    compact: false,
    highlight: false,
    deleted: false,
    annotated: false
};

ExternalResourceLink.propTypes = {
    highlight: PropTypes.bool,
    deleted: PropTypes.bool,
    dataTest: PropTypes.string,
    annotated: PropTypes.bool,
    compact: PropTypes.bool,
    superCompact: PropTypes.bool,
    sourceGraph: PropTypes.string,
    id: PropTypes.string.isRequired,
    onDeleteIRI: PropTypes.func,
    graph: PropTypes.string,
    propertyGraph: PropTypes.string,
    invalid: PropTypes.bool,
    source: PropTypes.string,
    isReadOnly: PropTypes.bool,
    property: PropTypes.string,
    classes: PropTypes.object
};

export default withRouter(withStyles(styles)(ExternalResourceLink));